export const ACTIVE_KEY = 'casino.active_session';

export interface ActiveSession {
  session_id: string;
  table_key: string;
  last_seq: number;
  started_at: number;
}

export function readActiveSession(): ActiveSession | null {
  const raw = localStorage.getItem(ACTIVE_KEY);
  if (!raw) return null;
  try {
    const v = JSON.parse(raw) as Partial<ActiveSession>;
    if (typeof v.session_id !== 'string') return null;
    if (typeof v.table_key !== 'string') return null;
    if (typeof v.last_seq !== 'number') return null;
    if (typeof v.started_at !== 'number') return null;
    return v as ActiveSession;
  } catch {
    return null;
  }
}

/**
 * crypto.randomUUID is only available in secure contexts (HTTPS / localhost).
 * On plain-HTTP LAN access from a phone (e.g. 192.168.x.x), iOS Safari
 * returns undefined and calling it throws. Fall back to getRandomValues,
 * which is available in every context. Final Math.random fallback for the
 * paranoid case where even getRandomValues is missing.
 */
export function randomUuid(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  const bytes = new Uint8Array(16);
  if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
    crypto.getRandomValues(bytes);
  } else {
    for (let i = 0; i < 16; i++) bytes[i] = Math.floor(Math.random() * 256);
  }
  bytes[6] = (bytes[6]! & 0x0f) | 0x40;   // version 4
  bytes[8] = (bytes[8]! & 0x3f) | 0x80;   // variant
  const h = Array.from(bytes, (b) => b.toString(16).padStart(2, '0'));
  return `${h.slice(0, 4).join('')}-${h.slice(4, 6).join('')}-${h.slice(6, 8).join('')}-${h.slice(8, 10).join('')}-${h.slice(10, 16).join('')}`;
}

export function startActiveSession(table_key: string): ActiveSession {
  const existing = readActiveSession();
  if (existing && existing.table_key === table_key) return existing;
  const fresh: ActiveSession = {
    session_id: randomUuid(),
    table_key,
    last_seq: 0,
    started_at: Date.now(),
  };
  localStorage.setItem(ACTIVE_KEY, JSON.stringify(fresh));
  return fresh;
}

export function endActiveSession(): void {
  localStorage.removeItem(ACTIVE_KEY);
}

export function updateLastSeq(next: number): void {
  const cur = readActiveSession();
  if (!cur) return;
  const updated: ActiveSession = { ...cur, last_seq: next };
  localStorage.setItem(ACTIVE_KEY, JSON.stringify(updated));
}
